import React from 'react'

import { Box } from '@mui/material'

import { camelize } from 'inflection'

const handler = e => e.stopPropagation()
const EVENTS = ['click', 'focus', 'mouse_down', 'mouse_up', 'pointer_down', 'pointer_up', 'touch_end', 'touch_start']
const HANDLERS = EVENTS.reduce((handlers, event) => ({
  ...handlers,
  [`on${camelize(event)}`]: handler
}), {})
/**
 * Wraps form controls and prevents their click and focus events from propagating to parents
 * @component
 * @alias SafeControl
 * @named_export Component
 * @example
 * import { faker } from '@faker-js/faker'
 * import { Accordion, AccordionSummary, AccordionDetails, FormControlLabel, Radio, RadioGroup } from '@mui/material'
 * import { ExpandMore } from '@mui/icons-material'
 * {() => {
 *   const choices = React.useMemo(() => ({ first: faker.lorem.paragraph(), second: faker.lorem.paragraph() }), [])
 *   const [choice, setChoice] = React.useState('first')
 *   const [expanded, setExpanded] = React.useState(true)
 *   return (
 *     <Accordion expanded={expanded} onChange={() => setExpanded(old => !old)}>
 *       <AccordionSummary expandIcon={<ExpandMore />}>
 *         <Box display="flex" alignItems="center" overflow="hidden" style={{ gap: '1rem' }}>
 *           <T.Bold style={{ flex: '0 0 auto' }}>Expandable Card</T.Bold>
 *           {expanded ? (
 *               <SafeControl>
 *                 <RadioGroup row value={choice} onChange={e => setChoice(e.target.value)}>
 *                   <FormControlLabel label="First" value="first" control={<Radio />} />
 *                   <FormControlLabel label="Second" value="second" control={<Radio />} />
 *                 </RadioGroup>
 *               </SafeControl>
 *           ) : (
 *             <Box display="flex" alignItems="center" maxWidth="50%" style={{ gap: '1rem' }}>
 *               <T.Body style={{ flex: '0 0 auto' }}>{choice}</T.Body>
 *               <T.Tiny noWrap style={{ flex: '1 1 100%' }}>{choices[choice]}</T.Tiny>
 *             </Box>
 *           )}
 *         </Box>
 *       </AccordionSummary>
 *       <AccordionDetails>
 *         <T.Body>{choice}: {choices[choice]}</T.Body>
 *       </AccordionDetails>
 *     </Accordion>
 *   )
 * }}
 */
export const Component = React.forwardRef(({ children, display = 'contents', ...passthru }, ref) => (
  <Box component="span" data-safe-control="true" display={display} ref={ref} {...HANDLERS} {...passthru}>
    {children}
  </Box>
))
Component.displayName = 'SafeControl'
Component.propTypes = Box.propTypes

export default React.memo(Component)
